import {
  ErrorObjectWithMessage,
  HandleableError,
} from "@components/error-boundary/error-types";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { HttpUtils } from "@stellar/utils";

/**
 * @returns Whether the provided data is an object with a message property.
 * @param data The data to check.
 */
function isErrorObjectWithMessage(
  data: unknown
): data is ErrorObjectWithMessage {
  return (
    typeof data === "object" &&
    data !== null &&
    "message" in data &&
    typeof data.message === "string"
  );
}

/**
 * Converts errors of unknown shape into a displayable JSX element.
 */
export function getErrorDisplayMarkup(error: HandleableError): string {
  // Added a try-catch block in case an exception is thrown while attempting to read the error
  try {
    // If error comes from one of our Stellar http clients
    if (HttpUtils.isFetchError(error)) {
      // If error comes from the Core API client return the message or error_v2 property
      if (FaroCoreApiUtils.Client.isResponseError(error.errorBody)) {
        const errorBody = error.errorBody;
        return errorBody.message || errorBody.error_v2;
      }

      // If error body is an object with a message property
      if (isErrorObjectWithMessage(error.errorBody)) {
        return error.errorBody.message;
      }

      // Otherwise default to the FetchError message
      return error.message;
    }

    // If error is a generic error
    if (error instanceof Error) {
      return error.message;
    }

    // If error is an object with a message property. Example: SerializedError from Redux Toolkit
    if (isErrorObjectWithMessage(error)) {
      return error.message;
    }

    // If error is a string
    if (typeof error === "string") {
      return error;
    }
  } catch (_) {
    return "An unknown error occurred.";
  }

  // If error is of unknown shape return a default message
  return "An unknown error occurred.";
}

/** List of error messages to skip sending to Sentry */
const errorsToSkipSentry = [
  /**
   * When a user tries to access a resource that does not exist like adding a random companyId in the route
   * An example of this error in Sentry: https://faro-technologies.sentry.io/issues/7013463200
   */
  "not_found_error",

  /**
   * When a user is logged in but not authenticated or their session has expired to access AdminPanel.
   * An example of this error in Sentry: https://faro-technologies.sentry.io/issues/6963046539
   */
  "authentication_error",
] as const;

/** Type representing errors that should be skipped for Sentry */
type ErrorsToSkipSentry = (typeof errorsToSkipSentry)[number];

/** Whether the error should be skipped for Sentry reporting */
export function shouldSkipSentryForError(error: HandleableError): boolean {
  return (
    HttpUtils.isFetchError(error) &&
    FaroCoreApiUtils.Client.isResponseError(error.errorBody) &&
    errorsToSkipSentry.includes(error.errorBody.error_v2 as ErrorsToSkipSentry)
  );
}
